"use client";

import * as React from "react";
import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { Button } from "./button";

// Types
interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  inputLabel?: string;
  inputPlaceholder?: string;
  selectLabel?: string;
  selectOptions?: { value: string; label: string }[];
}

export interface ConfirmResult {
  confirmed: true;
  input?: string;
  select?: string;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<false | ConfirmResult>;
}

// Context
const ConfirmContext = createContext<ConfirmContextValue | null>(null);

// Provider component
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [selectValue, setSelectValue] = useState("");
  const resolveRef = useRef<((value: false | ConfirmResult) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<false | ConfirmResult> => {
    setOptions(opts);
    setInputValue("");
    setSelectValue(opts.selectOptions?.[0]?.value ?? "");
    setIsOpen(true);
    // Trigger animation after mount
    requestAnimationFrame(() => setIsAnimating(true));

    return new Promise<false | ConfirmResult>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const closeModal = useCallback((result: false | ConfirmResult) => {
    setIsAnimating(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsOpen(false);
      resolveRef.current?.(result);
      resolveRef.current = null;
    }, 150);
  }, []);

  const handleConfirm = useCallback(() => {
    const result: ConfirmResult = { confirmed: true };
    if (options?.inputLabel) result.input = inputValue;
    if (options?.selectOptions) result.select = selectValue;
    closeModal(result);
  }, [closeModal, options, inputValue, selectValue]);

  const handleCancel = useCallback(() => {
    closeModal(false);
  }, [closeModal]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleCancel]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/60 transition-opacity duration-150 ${
              isAnimating ? "opacity-100" : "opacity-0"
            }`}
            onClick={handleCancel}
          />
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div
              className={`relative w-full max-w-[320px] md:max-w-[440px] rounded-2xl bg-card p-6 shadow-xl transition-all duration-150 ${
                isAnimating
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Content */}
              <div className="text-center">
                <h2 className="text-xl md:text-lg font-semibold text-foreground">
                  {options?.title}
                </h2>
                <p className="mt-2 text-sm md:text-xs font-medium text-foreground/85 leading-tight">
                  {options?.description}
                </p>
              </div>

              {options?.selectOptions && (
                <div className="mt-4 text-left">
                  {options.selectLabel && (
                    <label className="text-base md:text-sm font-medium text-foreground">
                      {options.selectLabel}
                    </label>
                  )}
                  <select
                    value={selectValue}
                    onChange={(e) => setSelectValue(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 h-10 md:h-9 text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {options.selectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {options?.inputLabel && (
                <div className="mt-4 text-left">
                  <label className="text-base md:text-sm font-medium text-foreground">
                    {options.inputLabel}
                  </label>
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={options.inputPlaceholder}
                    rows={3}
                    className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-base md:text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleCancel}
                >
                  {options?.cancelLabel || "Cancelar"}
                </Button>
                <Button
                  variant={options?.variant === "destructive" ? "destructive" : "default"}
                  className="flex-1"
                  onClick={handleConfirm}
                >
                  {options?.confirmLabel || "Continuar"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// Hook
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context.confirm;
}
